<template>
  <section>
    <header
      v-if="showHeader"
      :class="[$style.header, { [$style.noMarginBottom]: isDisplayingTitle }]"
    >
      <p
        :class="[$style.headerText, { [$style.headerTextTitle]: isDisplayingTitle }]"
      >
        {{ headerText }}
      </p>
      <IconTooltip
        v-if="tooltip && tooltip.text"
        :className="iconTooltipClassName"
        :iconClass="['jc-info-circle']"
      >
        <!-- DO NOT ALLOW ANY USER INPUT IN STRING PASSED TO v-html -->
        <!-- eslint-disable vue/no-v-html -->
        <p
          v-html="tooltip.text"
        />
        <!-- eslint-enable vue/no-v-html -->
      </IconTooltip>
      <FlatActionButton
        v-if="buttonText"
        :action="handleAddListItem"
        :class="[$style.addButton, addButtonClassName]"
        :disabled="disableAddListItem"
        :text="buttonText"
      />
      <div
        v-if="isDisplayingTitle"
        :class="$style.titleContainer"
      >
        <span :class="$style.listItemTitle">
          {{ nameTitle }}
        </span>
        <span :class="$style.listItemTitle">
          {{ valueTitle }}
        </span>
      </div>
    </header>
    <ul :class="[$style.list, { [$style.listSpacing]: isDisplayingTitle }]">
      <!-- TODO: Use transition styles for adding and removing list items! -->
      <li
        v-for="(listItem, index) in listItems"
        :key="listItem._id || listItem.tempId"
        :class="[
          $style.listItem,
          {
            [$style.listItemDynamicError]: shouldAddDynamicErrorClass(dynamicErrorList, index)
          },
          listItemClassNameOverride
        ]"
        :data-entry-key="`${listName}-[${index}]`"
      >
        <component
          :is="component"
          :addingItem="addingItem"
          :class="[$style.listItemFields, { [$style.listItemSpacing]: isDisplayingTitle }]"
          :dropdownLabel="labels(index).dropdown"
          :handleChangeListItem="(newListItem, columnName) => {
            handleChangeListItem(index, newListItem, columnName);
          }"
          :index="index"
          :inputLabel="labels(index).input"
          :listItem="listItem"
          :namePlaceholder="namePlaceholder"
          :nameReadOnly="listItem.nameReadOnly"
          :nameValidation="nameValidation"
          :readonly="listItem.readOnly"
          :showNameAttribute="showNameAttribute"
          :valueDynamicComponent="valueDynamicComponent"
          :valueDynamicComponentProps="valueDynamicComponentProps"
          :valueMaxLengthValidation="valueMaxLengthValidation"
          :valuePlaceholder="valuePlaceholder"
          :valueReadOnly="listItem.valueReadOnly"
          :valueValidation="valueValidation"
        />

        <button
          v-if="!listItem.toggleInput && !listItem.required"
          v-show="!listItem.readOnly"
          :class="[getRemoveButtonClassNames]"
          @click="handleRemoveListItem(index, listItem)"
        >
          <i :class="[$style.removeButtonIcon, 'jc-actions-trash', removeButtonClassName]" />
        </button>

        <div
          v-if="dynamicErrorList.length > 0
            && errorDefinition(dynamicErrorList, index)
            && errorDefinition(dynamicErrorList, index).shouldShow"
          :class="[$style.error, $style.dynamicError]"
        >
          {{ errorDefinition(dynamicErrorList, index).errorText }}
        </div>

        <PrettyToggle
          v-if="listItem.toggleInput && !listItem.required"
          :class="[$style.toggleCheckbox, toggle.toggleCheckbox]"
          :config="listItem.toggleInput"
          :handleToggleChange="() => handleItemToggleChange(listItem)"
        />
      </li>
    </ul>
  </section>
</template>
<script>
import CustomAttributeFields from 'client/components/FormValidation/CustomAttributeFields.vue';
import DatabaseAttributeFields from 'client/components/Applications/DatabaseAttributeFields.vue';
import FlatActionButton from 'client/components/Buttons/FlatActionButton.vue';
import IconTooltip from 'client/components/Tooltip/IconTooltip.vue';
import MembershipSuggestionRuleFields from 'client/components/FormValidation/MembershipSuggestionRuleFields.vue';
import PrettyToggle from 'client/components/FormValidation/PrettyToggle.vue';
import PublicKeyFields from 'client/systemusers/UserDetails/PublicKeyFields.vue';
import SingleListBoxFields from 'client/components/DynamicControls/BodyComponents/SingleListBoxFields.vue';

export default {
  name: 'ListEditor',

  components: {
    CustomAttributeFields,
    DatabaseAttributeFields,
    FlatActionButton,
    IconTooltip,
    MembershipSuggestionRuleFields,
    PrettyToggle,
    PublicKeyFields,
    SingleListBoxFields,
  },

  inject: ['$validator'],

  props: {
    addButtonClassName: {
      type: String,
      default: 'list-editor-add-button',
    },
    addingItem: {
      type: Boolean,
      default: false,
    },
    buttonText: {
      type: String,
      default: 'Add New List Item',
    },
    component: {
      type: [String, Object],
      required: true,
    },
    disableAddListItem: {
      type: Boolean,
      default: false,
    },
    dropdownLabel: {
      type: String,
      default: '',
    },
    dynamicErrorList: {
      type: Array,
      default: () => [],
    },
    handleAddListItem: {
      type: Function,
      default: () => {},
    },
    handleChangeListItem: {
      type: Function,
      required: true,
    },
    handleItemToggleChange: {
      type: Function,
      default: undefined,
    },
    handleRemoveListItem: {
      type: Function,
      required: true,
    },
    headerText: {
      type: String,
      required: true,
    },
    iconTooltipClassName: {
      type: String,
      default: 'icon-tooltip',
    },
    inputLabel: {
      type: String,
      default: '',
    },
    listItemClassNameOverride: {
      type: String,
      default: '',
    },
    listItems: {
      type: Array,
      required: true,
    },
    listName: {
      type: String,
      default: '',
    },
    namePlaceholder: {
      type: String,
      default: 'Attribute Name',
    },
    nameTitle: {
      type: String,
      default: '',
    },
    nameValidation: {
      type: String,
      default: 'alpha_num|max:32|required',
    },
    onlyTopLabels: {
      type: Boolean,
      default: true,
    },
    removeButtonClassName: {
      type: String,
      default: 'list-editor-remove-button',
    },
    removeButtonClassOverride: {
      type: String,
      default: undefined,
    },
    showHeader: {
      type: Boolean,
      default: true,
    },
    showNameAttribute: {
      type: Boolean,
      default: false,
    },
    tooltip: {
      type: Object,
      default: () => {},
    },
    valueDynamicComponent: {
      type: [String, Object],
      default: 'InputValidator',
    },
    valueDynamicComponentProps: {
      type: Object,
      default: null,
    },
    valueMaxLengthValidation: {
      type: String,
      default: '1,024',
    },
    valuePlaceholder: {
      type: String,
      default: 'Attribute Value',
    },
    valueTitle: {
      type: String,
      default: '',
    },
    valueValidation: {
      type: String,
      default: 'max:1024|required',
    },
  },

  computed: {
    getRemoveButtonClassNames() {
      const classNames = [this.$style.removeButton];
      if (this.showNameAttribute) {
        classNames.push(this.$style.rightAlign);
      }
      if (this.removeButtonClassOverride) {
        classNames.push(this.removeButtonClassOverride);
      }
      return classNames;
    },

    isDisplayingTitle() {
      return this.nameTitle && this.valueTitle;
    },
  },

  methods: {
    errorDefinition(errorList, index) {
      return errorList.find(error => error.errorIndex === index);
    },

    labels(index) {
      return ((index !== 0 && this.onlyTopLabels) ? { input: '', dropdown: '' }
        : { input: this.inputLabel, dropdown: this.dropdownLabel });
    },

    shouldAddDynamicErrorClass(dynamicErrorList, index) {
      return dynamicErrorList.length > 0
        && this.errorDefinition(dynamicErrorList, index)
        && this.errorDefinition(dynamicErrorList, index).shouldShow
        && this.errorDefinition(dynamicErrorList, index).errorIndex === index;
    },
  },
};
</script>
<style module="toggle">
.toggleCheckbox label.custom-control-label::before {
  margin-top: -1px;
}
</style>
<style module>
@import './css/ListEditor.css';
@import '../../components/FormValidation/css/formFields.css';
@import "../../components/FormValidation/css/FormValidationFields.css";

@value (jcError) from '~css/brand-colors.css';

.dynamicError {
  margin-bottom: 4px;
  margin-top: -14px;
  width: 45%;
}

.listItemDynamicError {
  display: initial;
}

.listItemDynamicError fieldset div:first-child div input {
  border-color: jcError;
}

.removeButton.rightAlign {
  right: -9px;
  top: 2px;
}
</style>
